import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    EventEmitter,
    Input,
    OnChanges,
    OnInit,
    Output,
    SimpleChanges
} from '@angular/core';
import {CommonModule} from '@angular/common';
import {FormsModule} from '@angular/forms';
import {MatSelectModule} from '@angular/material/select';
import {MatIconModule} from '@angular/material/icon';
import {MatButtonModule} from '@angular/material/button';
import {MatTooltipModule} from '@angular/material/tooltip';
import {MatMenuModule} from '@angular/material/menu'; // NEW IMPORT
import {DayOpeningHours, OpeningHoursData, OpeningHoursService, TimeSlot} from "../../services/opening-hours.service";

@Component({
    selector: 'app-opening-hours-input',
    templateUrl: './opening-hours-input.component.html',
    styleUrls: ['./opening-hours-input.component.scss'],
    standalone: true,
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [
        CommonModule,
        FormsModule,
        MatSelectModule,
        MatIconModule,
        MatButtonModule,
        MatTooltipModule,
        MatMenuModule // NEW
    ],
})
export class OpeningHoursInputComponent implements OnInit, OnChanges {
    @Input() value: OpeningHoursData | null | undefined;
    @Input() defaultOpenTime: string = "09:00 AM";
    @Input() defaultCloseTime: string = "05:00 PM";
    @Input() weekdaysOpenByDefault: boolean = true;

    @Output() valueChange = new EventEmitter<OpeningHoursData>();

    internalHours: OpeningHoursData;
    timeOptions: string[] = [];

    constructor(
        public openingHoursService: OpeningHoursService, // Changed to public to access constants if needed in template
        private _cdr: ChangeDetectorRef
    ) {}

    ngOnInit(): void {
        this.timeOptions = this.generateAmPmTimeOptions(30);
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes.value) {
            const externalValue = changes.value.currentValue;
            if (this.isValidOpeningHoursData(externalValue) && JSON.stringify(externalValue) !== JSON.stringify(this.internalHours)) {
                this.internalHours = JSON.parse(JSON.stringify(externalValue));
            } else if (!this.isValidOpeningHoursData(externalValue)) {
                this.initializeHours();
            }
            this._cdr.markForCheck();
        }
    }

    // ... (isValidOpeningHoursData, initializeHours, generateAmPmTimeOptions, formatMinutesToAmPm remain same)
    private isValidOpeningHoursData(data: any): data is OpeningHoursData {
        return Array.isArray(data) && data.length === 7 && 'day' in data[0] && 'timeSlots' in data[0];
    }

    private initializeHours(): void {
        let initialData = this.openingHoursService.getDefaultOpeningHours();
        if (this.weekdaysOpenByDefault) {
            const weekdayNames = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday"];
            initialData = initialData.map(day => {
                if (weekdayNames.includes(day.day)) {
                    return {
                        ...day,
                        isOpen: true,
                        timeSlots: [{ openTime: this.defaultOpenTime, closeTime: this.defaultCloseTime }]
                    };
                }
                return day;
            });
        }
        this.internalHours = initialData;
    }

    private generateAmPmTimeOptions(intervalMinutes: number = 30): string[] {
        const options: string[] = [];
        const minutesInDay = 24 * 60;
        for (let i = 0; i < minutesInDay; i += intervalMinutes) {
            const h = Math.floor(i / 60);
            const m = i % 60;
            options.push(this.formatMinutesToAmPm(h, m));
        }
        if (!options.includes("11:59 PM")) options.push("11:59 PM");
        return options;
    }

    private formatMinutesToAmPm(hours: number, minutes: number): string {
        const ampm = hours >= 12 ? 'PM' : 'AM';
        const displayHours = hours % 12 || 12;
        return `${displayHours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')} ${ampm}`;
    }

    private emitChanges(): void {
        this.valueChange.emit(this.internalHours);
    }

    // --- UX ACTIONS ---

    handleToggleOpen(dayIndex: number): void {
        const day = this.internalHours[dayIndex];
        day.isOpen = !day.isOpen;
        if (day.isOpen && day.timeSlots.length === 0) {
            day.timeSlots = [{ openTime: this.defaultOpenTime, closeTime: this.defaultCloseTime }];
        } else if (!day.isOpen) {
            day.timeSlots = [];
        }
        this.emitChanges();
    }

    // Helper to detect if a day is set to 24h
    isDay24Hours(day: DayOpeningHours): boolean {
        if (!day.isOpen || day.timeSlots.length !== 1) return false;
        const slot = day.timeSlots[0];
        return slot.openTime === this.openingHoursService.TIME_24H_START &&
            slot.closeTime === this.openingHoursService.TIME_24H_END;
    }

    // Sets a specific day to 24 hours
    set24Hours(dayIndex: number): void {
        const day = this.internalHours[dayIndex];
        day.isOpen = true;
        day.timeSlots = [{
            openTime: this.openingHoursService.TIME_24H_START,
            closeTime: this.openingHoursService.TIME_24H_END
        }];
        this.emitChanges();
    }

    // Bulk Copy Logic
    copyHoursTo(sourceDay: DayOpeningHours, target: 'all' | 'weekdays' | 'weekends'): void {
        const weekdays = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday'];
        const weekends = ['Saturday', 'Sunday'];

        this.internalHours = this.internalHours.map(d => {
            // Don't copy to self
            if (d.day === sourceDay.day) return d;

            let shouldCopy = false;
            if (target === 'all') shouldCopy = true;
            if (target === 'weekdays' && weekdays.includes(d.day)) shouldCopy = true;
            if (target === 'weekends' && weekends.includes(d.day)) shouldCopy = true;

            if (shouldCopy) {
                // deep copy the timeslots to avoid reference issues
                const slotsCopy = sourceDay.timeSlots.map(s => ({ ...s }));
                return {
                    ...d,
                    isOpen: sourceDay.isOpen,
                    timeSlots: slotsCopy
                };
            }
            return d;
        });

        this.emitChanges();
    }

    // ... (handleAddSlot, handleRemoveSlot, handleTimeChange remain same)
    handleAddSlot(dayIndex: number): void {
        const day = this.internalHours[dayIndex];
        if (day.timeSlots.length < 2) {
            day.timeSlots.push({ openTime: this.defaultOpenTime, closeTime: this.defaultCloseTime });
            this.emitChanges();
        }
    }

    handleRemoveSlot(dayIndex: number, slotIndex: number): void {
        const day = this.internalHours[dayIndex];
        day.timeSlots.splice(slotIndex, 1);
        if (day.timeSlots.length === 0) {
            day.isOpen = false;
        }
        this.emitChanges();
    }

    handleTimeChange(day: DayOpeningHours, slot: TimeSlot, type: 'openTime' | 'closeTime', time: string): void {
        slot[type] = time;
        const openIdx = this.timeOptions.indexOf(slot.openTime);
        const closeIdx = this.timeOptions.indexOf(slot.closeTime);

        if (type === 'openTime' && openIdx >= closeIdx && closeIdx !== -1) {
            if (openIdx < this.timeOptions.length - 1) {
                slot.closeTime = this.timeOptions[openIdx + 1];
            } else {
                slot.closeTime = this.timeOptions[this.timeOptions.length - 1];
            }
        } else if (type === 'closeTime' && closeIdx <= openIdx && openIdx !== -1) {
            if (closeIdx > 0) {
                slot.openTime = this.timeOptions[closeIdx - 1];
            } else {
                slot.openTime = this.timeOptions[0];
            }
        }
        this.emitChanges();
    }
}
